/**
 * Editor — the single-map view. Ports mockups/03-editor.html's 3-column layout:
 * groups panel (left), Leaflet map canvas (center), Porter placeholder (right),
 * under an editor topbar (back link, editable title, client badge, basemap
 * switcher, Export/Share).
 *
 * Save model: document-style autosave (decision 1). The whole map document lives
 * in one piece of state; every edit marks it dirty and schedules a debounced
 * (~1.5s) PUT /api/maps/:id of the entire document. The topbar's .saved-note
 * reflects Saving… / Saved just now / a save error, matching the mock-up.
 *
 * This slice owns groups + areas + basemap + title. Porter, exports, datasets,
 * and the canvas draw tools are separate slices — the Export/Share buttons and
 * draw toolbar are present per the mock-up but wired minimally here.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Pencil,
  Briefcase,
  Check,
  Download,
  Share2,
  Plus,
  Loader2,
  PencilLine,
  Square,
  CircleDot,
  ChevronDown,
  Search,
  X,
  Database,
} from 'lucide-react';
import api from '../utils/api';
import MapCanvas from '../components/MapCanvas';
import GroupCard from '../components/GroupCard';
import PorterPanel from '../components/PorterPanel';
import ExportModal from '../components/ExportModal';
import useShapeMetrics from '../hooks/useShapeMetrics';
import { useToast } from '../context/ToastContext';
import { formatNumber, areaLabel } from '../utils/format';
import { areaToPolygon } from '../utils/geometry';
import './Editor.css';

// Default palette for new groups — the mock-up's group colors, cycled.
const GROUP_PALETTE = ['#2563EB', '#DC2626', '#16A34A', '#9333EA', '#EA580C', '#0891B2', '#CA8A04'];

// Basemap options for the topbar segmented control. Values match the document's
// basemap enum (voyager|streets|light|dark). "voyager" (CARTO Voyager) is the
// document default new maps are created with (mapsService.createMap), so it MUST
// appear here or a brand-new map shows no active segment — labeled "Streets" as
// the friendly default, with the raw OSM "streets" style offered as "OSM". The set
// is kept identical to Settings.js's basemap dropdown for consistency.
const BASEMAP_OPTIONS = [
  { value: 'voyager', label: 'Streets' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'streets', label: 'OSM' },
];

const AUTOSAVE_DELAY_MS = 1500;

// Above this many total areas, a save can genuinely take long enough (network
// + server-side geometry resolution) that the small topbar "Saving…" label
// isn't enough on its own — a toast makes it much harder to miss.
const LARGE_SAVE_AREA_COUNT = 100;

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast, dismiss } = useToast();

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // 'saved' | 'dirty' | 'saving' | 'error'
  const [saveStatus, setSaveStatus] = useState('saved');
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [showExport, setShowExport] = useState(false);
  // GeoJSON geometry of the most recent free-form shape (polygon/rectangle) the
  // user drew on the map, or null. Set by handleShapeDrawn below. This is the
  // FALLBACK for porterAreaContext (below) when nothing is selected — see
  // that memo for the actual value passed to PorterPanel. drawnAreaLabel is
  // the group name at the moment it was drawn, carried alongside purely for
  // display (PorterPanel's "using X" indicator) — it never reaches the server.
  const [drawnArea, setDrawnArea] = useState(null);
  const [drawnAreaLabel, setDrawnAreaLabel] = useState(null);

  // The shape currently selected on the map canvas (click-to-select addendum),
  // or null. Lifted here (rather than owned inside MapCanvas) so the owning
  // GroupCard can mirror the same is-selected outline — same reasoning as
  // expandedGroupId below. { groupId, areaId } | null.
  const [selectedArea, setSelectedArea] = useState(null);

  // The area Porter treats as "the drawn area" (get_drawn_area /
  // find_areas_in_area's use_drawn_area) — the server keeps no session state,
  // so this rides along on every chat request. Prefers whichever shape is
  // currently SELECTED, of any type (radius/drawn/census-backed): with
  // several shapes on the map, the user designates which one by clicking it
  // first, and a selected radius converts to a polygon via areaToPolygon
  // (radius circles were previously invisible to Porter entirely). Falls back
  // to the last freeform shape actually drawn when nothing is selected, so
  // the simple "draw one shape and ask" flow keeps working unchanged. Because
  // this is computed fresh from doc on every render (not a separately-tracked
  // copy), reshaping a selected area also keeps this in sync automatically.
  //
  // Bundled with a human-readable `label` (the owning group's name) so
  // PorterPanel can show the user WHICH area is currently in play — the
  // mechanism alone was invisible in the UI, which is exactly what caused
  // continued confusion the first time this shipped: the fix worked, but
  // nothing on screen ever confirmed it or said which shape "wins".
  const porterAreaContext = useMemo(() => {
    if (selectedArea) {
      const group = doc?.groups?.find((g) => g.id === selectedArea.groupId);
      const area = group?.areas?.find((a) => a.id === selectedArea.areaId);
      const polygon = area ? areaToPolygon(area) : null;
      if (polygon) return { geometry: polygon, label: group.name || 'Selected area' };
    }
    if (drawnArea) return { geometry: drawnArea, label: drawnAreaLabel || 'the shape you last drew' };
    return null;
  }, [selectedArea, doc, drawnArea, drawnAreaLabel]);

  // Imperative handle into MapCanvas for starting Geoman draw modes from the
  // toolbar buttons. Null until the canvas mounts; each trigger no-ops safely.
  const mapCanvasRef = useRef(null);

  // Size + population readouts for radius/drawn groups (approved shape-labels
  // addendum). Keyed by group id; census-backed groups are absent. Called
  // unconditionally at the top level (rules of hooks) with the current groups —
  // the hook no-ops on an undefined/empty list before the doc loads.
  const shapeMetrics = useShapeMetrics(doc?.groups);

  // Debounce timer + a guard so the initial load doesn't trigger a save.
  const saveTimer = useRef(null);
  const skipNextSave = useRef(true);
  // Always-current doc for the debounced saver to read without stale closure.
  const docRef = useRef(null);
  docRef.current = doc;

  // ─── Load the map document ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    skipNextSave.current = true;
    api
      .get(`/maps/${id}`)
      .then((data) => {
        if (cancelled) return;
        setDoc(data);
        setLoadError(null);
        setSaveStatus('saved');
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 404) setLoadError('This map could not be found.');
        else setLoadError(err.message || 'Could not load this map.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [id]);

  // ─── Persist the whole document (used by the debounced autosave) ──────────
  const persist = useCallback(async () => {
    const current = docRef.current;
    if (!current) return;
    setSaveStatus('saving');

    // A large save gets a toast in addition to the topbar label — dismissed
    // the moment this save actually resolves (success or error) below, not
    // on a guessed timer, so it never lingers past or lags behind real work.
    // A generous duration is still passed as a safety net in case dismiss()
    // is somehow never reached (e.g. an unmount mid-save).
    const areaCount = (current.groups || []).reduce((n, g) => n + (g.areas?.length || 0), 0);
    const toastId =
      areaCount > LARGE_SAVE_AREA_COUNT
        ? showToast(`Saving ${formatNumber(areaCount)} areas — this can take a little while for a large map…`, {
            variant: 'info',
            duration: 120000,
          })
        : null;

    try {
      const saved = await api.put(`/maps/${current.id}`, current);
      // Adopt the server's authoritative timestamps/ids but keep the user's
      // in-flight edits from being clobbered: only merge server metadata, not
      // the whole doc, if the user kept typing. Simplest correct approach here —
      // the server echoes the saved doc; if nothing changed since we sent it,
      // adopt it wholesale so client ids become server ids.
      if (docRef.current === current) {
        // Adopting the server response replaces the doc (client ids → server
        // ids, fresh updated_at). That state change must NOT re-arm autosave, or
        // every save would schedule another — flag the next doc effect to skip.
        skipNextSave.current = true;
        // Every group/area just got a new id (saveMap is a document-style
        // replace) — re-point anything that referenced the old ones before
        // adopting, or a selection/expanded-card silently orphans the moment
        // this save lands (see remapGroupId/remapAreaSelection above).
        setSelectedArea((prev) => remapAreaSelection(current, saved, prev));
        setExpandedGroupId((prev) => remapGroupId(current, saved, prev));
        setDoc(saved);
        setSaveStatus('saved');
      } else {
        // User edited again mid-flight; a follow-up save is already scheduled.
        setSaveStatus('dirty');
      }
    } catch (err) {
      setSaveStatus('error');
      showToast(err.message || 'Save failed — retrying on next change.', { variant: 'error' });
    } finally {
      if (toastId != null) dismiss(toastId);
    }
  }, [showToast, dismiss]);

  // ─── Debounced autosave: reschedule whenever the doc changes ──────────────
  useEffect(() => {
    if (skipNextSave.current) {
      // The change that set skipNextSave (initial load / post-save adoption)
      // must not trigger another save.
      skipNextSave.current = false;
      return;
    }
    if (!doc) return;
    setSaveStatus('dirty');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(persist, AUTOSAVE_DELAY_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [doc, persist]);

  // Immutable document update helper — pass a function that returns the next doc.
  const updateDoc = useCallback((mutator) => {
    setDoc((prev) => (prev ? mutator(prev) : prev));
  }, []);

  // ─── Group + area mutations ───────────────────────────────────────────────
  const addGroup = useCallback(() => {
    updateDoc((prev) => {
      const nextIndex = prev.groups.length;
      const color = GROUP_PALETTE[nextIndex % GROUP_PALETTE.length];
      const newGroup = {
        id: mintId(),
        name: `Group ${nextIndex + 1}`,
        color,
        fill_opacity: 0.45,
        show_labels: false,
        merge_shapes: false,
        is_visible: true,
        source_dataset_id: null,
        sort_order: nextIndex,
        areas: [],
      };
      // Auto-expand the new group so the user can start adding areas immediately.
      setExpandedGroupId(newGroup.id);
      return { ...prev, groups: [...prev.groups, newGroup] };
    });
  }, [updateDoc]);

  const updateGroup = useCallback(
    (groupId, patch) => {
      updateDoc((prev) => ({
        ...prev,
        groups: prev.groups.map((g) => (g.id === groupId ? { ...g, ...patch } : g)),
      }));
    },
    [updateDoc]
  );

  // Append a fully-built group (already carrying its areas) to the document.
  // Porter's "Add to map" uses this: it constructs the group + one area per
  // result row (matching the manual-entry area shape), then hands it here so it
  // flows through the SAME autosave path as any other edit — the server resolves
  // real geometry on save, exactly like manually typed areas. No separate save
  // call. The new group's sort_order is normalized to its final index in case the
  // caller computed it from a stale count.
  const addGroupWithAreas = useCallback(
    (group) => {
      updateDoc((prev) => ({
        ...prev,
        groups: [...prev.groups, { ...group, sort_order: prev.groups.length }],
      }));
    },
    [updateDoc]
  );

  // Add an already-saved dataset to this map as a new group (the retroactive
  // counterpart to Porter's immediate "Add to map" — for a dataset saved
  // earlier, e.g. via "Save as dataset", that never got added at that moment).
  // Unlike every other group mutation here, this is resolved server-side
  // (POST /api/maps/:id/datasets), since converting dataset rows into areas
  // needs boundary-cache resolution the client has no business doing — so the
  // response is a fresh, already-saved document adopted wholesale, the same
  // way persist() adopts the server's response after a PUT (skipNextSave
  // guards against that adoption re-triggering autosave).
  const addDatasetToMap = useCallback(
    async (datasetId) => {
      try {
        const saved = await api.post(`/maps/${doc.id}/datasets`, { dataset_id: datasetId });
        skipNextSave.current = true;
        setDoc(saved);
        setSaveStatus('saved');
        // The new group's name is the dataset's name (mapsService.addDatasetToMap) —
        // read it back from the response rather than threading it through as a
        // second argument, so the toast reflects what the server actually named it.
        const newGroup = [...(saved.groups || [])].reverse().find((g) => g.source_dataset_id === datasetId);
        showToast(`Added "${newGroup?.name || 'dataset'}" to the map`);
      } catch (err) {
        showToast(err.message || 'Could not add that dataset to the map.', { variant: 'error' });
        throw err; // let DatasetPicker know the add failed, so it keeps its popover open
      }
    },
    [doc?.id, showToast]
  );

  // ─── Canvas drawing (Geoman) ──────────────────────────────────────────────
  // Called by MapCanvas when the user finishes a polygon/rectangle (kind:'drawn')
  // or a circle (kind:'radius'). Each finished shape becomes its own new group —
  // consistent with how Porter's "Add to map" adds a fresh group rather than
  // folding into an existing one, and it keeps the group's color/legend entry
  // meaningful for a single drawn shape. Groups are auto-named (no prompt): a
  // name dialog on every stroke would break the fast draw-and-go flow, and the
  // user can rename via the group card. Naming mirrors the mock-up's
  // "Expansion — 25mi radius" style and GroupCard's radius label. The new group
  // flows through addGroupWithAreas → the same debounced autosave as every other
  // edit (the server persists 'drawn'/'radius' areas directly — no geometry
  // resolution needed for these, unlike census areas).
  const handleShapeDrawn = useCallback(
    ({ kind, area }) => {
      const groups = docRef.current?.groups || [];
      const nextIndex = groups.length;
      const color = GROUP_PALETTE[nextIndex % GROUP_PALETTE.length];

      let groupName;
      let label;
      if (kind === 'radius') {
        // radius_mi is already rounded to ≤2 decimals in MapCanvas; a template
        // literal renders 5 as "5" and 5.5 as "5.5" — no extra formatting needed.
        const miles = area.params?.radius_mi ?? 0;
        groupName = `Radius — ${miles}mi`;
        label = groupName;
      } else {
        // Count existing drawn groups so successive polygons read "Drawn area 1",
        // "Drawn area 2", … regardless of other (radius/census) groups present.
        const drawnCount = groups.filter((g) =>
          (g.areas || []).some((a) => a.area_type === 'drawn')
        ).length;
        groupName = `Drawn area ${drawnCount + 1}`;
        label = groupName;
      }

      const newArea = {
        id: mintId(),
        area_type: area.area_type,
        label,
        geoid: null,
        boundary_id: null,
        geometry: area.geometry || {},
        params: area.params || {},
        sort_order: 0,
      };
      const group = {
        id: mintId(),
        name: groupName,
        color,
        fill_opacity: 0.45,
        show_labels: false,
        merge_shapes: false,
        is_visible: true,
        source_dataset_id: null,
        sort_order: nextIndex,
        areas: [newArea],
      };
      addGroupWithAreas(group);

      // Only free-form drawn shapes populate drawnArea (Porter's get_drawn_area).
      // A radius is a distinct area type, not "the shape you drew", so it
      // doesn't — though selecting a radius afterward still works via
      // porterAreaContext above.
      if (kind === 'drawn') {
        setDrawnArea(area.geometry || null);
        setDrawnAreaLabel(groupName);
      }

      showToast(`Added "${groupName}" to the map`);
    },
    [addGroupWithAreas, showToast]
  );

  // Toolbar handlers — each starts one Geoman draw mode on the canvas. Guarded so
  // clicking before the map mounts is a harmless no-op. Also clears any selected
  // shape first — leaving its popup anchored to the old shape while a new one is
  // being drawn would just be clutter.
  const startDrawPolygon = useCallback(() => {
    setSelectedArea(null);
    mapCanvasRef.current?.drawPolygon();
  }, []);
  const startDrawRectangle = useCallback(() => {
    setSelectedArea(null);
    mapCanvasRef.current?.drawRectangle();
  }, []);
  const startDrawCircle = useCallback(() => {
    setSelectedArea(null);
    mapCanvasRef.current?.drawCircle();
  }, []);

  const deleteGroup = useCallback(
    (groupId) => {
      const group = docRef.current?.groups.find((g) => g.id === groupId);
      const ok = window.confirm(`Delete group "${group?.name || ''}"? Its areas are removed too.`);
      if (!ok) return;
      updateDoc((prev) => ({
        ...prev,
        groups: prev.groups.filter((g) => g.id !== groupId),
      }));
      setSelectedArea((prev) => (prev && prev.groupId === groupId ? null : prev));
    },
    [updateDoc]
  );

  const addAreas = useCallback(
    (groupId, newAreas) => {
      updateDoc((prev) => ({
        ...prev,
        groups: prev.groups.map((g) =>
          g.id === groupId ? { ...g, areas: [...(g.areas || []), ...newAreas] } : g
        ),
      }));
    },
    [updateDoc]
  );

  // Shared by the sidebar chip's remove button AND the map's click-to-select
  // Delete action — a single area, not a whole group, so it previously had no
  // confirmation at all (unlike deleteGroup below); adding it here covers both
  // entry points at once rather than duplicating the prompt in MapCanvas.
  const removeArea = useCallback(
    (groupId, areaId) => {
      const group = docRef.current?.groups.find((g) => g.id === groupId);
      const area = group?.areas.find((a) => a.id === areaId);
      const ok = window.confirm(`Delete ${area ? areaLabel(area) : 'this area'}?`);
      if (!ok) return;
      updateDoc((prev) => ({
        ...prev,
        groups: prev.groups.map((g) =>
          g.id === groupId ? { ...g, areas: (g.areas || []).filter((a) => a.id !== areaId) } : g
        ),
      }));
      setSelectedArea((prev) => (prev && prev.groupId === groupId && prev.areaId === areaId ? null : prev));
    },
    [updateDoc]
  );

  // Radius-edit popup commit (click-to-select addendum) — patches just the
  // radius value in place; the area keeps its id, so selection stays valid and
  // useShapeMetrics picks up the changed signature to refresh population.
  const editAreaRadius = useCallback(
    (groupId, areaId, radiusMi) => {
      updateDoc((prev) => ({
        ...prev,
        groups: prev.groups.map((g) =>
          g.id !== groupId
            ? g
            : {
                ...g,
                areas: (g.areas || []).map((a) =>
                  a.id === areaId ? { ...a, params: { ...a.params, radius_mi: radiusMi } } : a
                ),
              }
        ),
      }));
    },
    [updateDoc]
  );

  // "Done reshaping" commit (click-to-select addendum) — patches just the
  // geometry in place, once, after Geoman's vertex-drag session ends.
  const commitAreaReshape = useCallback(
    (groupId, areaId, geometry) => {
      updateDoc((prev) => ({
        ...prev,
        groups: prev.groups.map((g) =>
          g.id !== groupId
            ? g
            : {
                ...g,
                areas: (g.areas || []).map((a) => (a.id === areaId ? { ...a, geometry } : a)),
              }
        ),
      }));
    },
    [updateDoc]
  );

  const toggleExpand = useCallback((groupId) => {
    setExpandedGroupId((cur) => (cur === groupId ? null : groupId));
  }, []);

  const setBasemap = useCallback(
    (value) => {
      updateDoc((prev) => ({ ...prev, basemap: value }));
    },
    [updateDoc]
  );

  // Assign (or clear) the map's client. Sets both client_id (persisted; flows
  // through the same PUT /api/maps/:id autosave — validateMapDocument/saveMap
  // already accept client_id) and client_name (display-only, so the topbar badge
  // updates immediately without waiting for the save round-trip to echo it back).
  const setClient = useCallback(
    (client) => {
      updateDoc((prev) => ({
        ...prev,
        client_id: client ? client.id : null,
        client_name: client ? client.name : null,
      }));
    },
    [updateDoc]
  );

  // ─── Title editing ────────────────────────────────────────────────────────
  const startRename = () => {
    setTitleDraft(doc?.name || '');
    setEditingTitle(true);
  };
  const commitRename = () => {
    const name = titleDraft.trim() || 'Untitled map';
    updateDoc((prev) => ({ ...prev, name }));
    setEditingTitle(false);
  };

  // ─── Share (internal link) ────────────────────────────────────────────────
  // Per the decision log: "Share = copy internal link (SSO-protected)."
  // Client-facing tokenized links are deferred pending security review, so this
  // is deliberately simple: the whole app is SSO-gated on every route, so the map
  // URL itself IS the access control — copy it and anyone signed in can open it.
  // window.location.origin is the production origin (maps.frontporchsolutions.com)
  // and localhost in dev, so the copied link is always same-origin/correct.
  const handleShare = useCallback(async () => {
    const url = `${window.location.origin}/maps/${id}`;
    const confirmMsg = 'Link copied — anyone signed in can view this map';
    try {
      await navigator.clipboard.writeText(url);
      showToast(confirmMsg);
    } catch {
      // Clipboard API can be blocked (insecure context / permissions). Fall back
      // to a hidden textarea + execCommand so the copy still works, matching
      // GroupCard's copy-codes fallback.
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        showToast(confirmMsg);
      } catch {
        showToast('Copy failed — copy the map URL from your address bar.', { variant: 'error' });
      }
      document.body.removeChild(ta);
    }
  }, [id, showToast]);

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="main editor-loading">
        <Loader2 className="spin" size={22} />
        <span>Loading map…</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="main editor-loading">
        <div className="callout callout-error" role="alert" style={{ maxWidth: 420 }}>
          <div>
            <div className="callout-title">Map unavailable</div>
            {loadError}
          </div>
        </div>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/maps')}>
          Back to My Maps
        </button>
      </div>
    );
  }

  const groups = doc.groups || [];

  return (
    <div className="main editor-main">
      <div className="editor-topbar">
        <button className="back-link" onClick={() => navigate('/maps')}>
          Maps
        </button>

        <div className="map-title-wrap">
          {editingTitle ? (
            <input
              className="input map-title-input"
              value={titleDraft}
              autoFocus
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') setEditingTitle(false);
              }}
            />
          ) : (
            <>
              <h1 className="map-title">{doc.name || 'Untitled map'}</h1>
              <button className="rename" title="Rename map" onClick={startRename}>
                <Pencil size={13} />
              </button>
            </>
          )}

          {/* Client picker — assigns the map to a Teamwork-synced client. The
              choice flows through autosave (setClient → PUT /api/maps/:id). */}
          <ClientPicker
            clientId={doc.client_id || null}
            clientName={doc.client_name || null}
            onSelect={setClient}
          />

          <SavedNote status={saveStatus} />
        </div>

        <div className="segmented" role="group" aria-label="Basemap">
          {BASEMAP_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`segmented-option${doc.basemap === opt.value ? ' is-active' : ''}`}
              onClick={() => setBasemap(opt.value)}
              aria-pressed={doc.basemap === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Export opens the export modal (CSV/GeoJSON download now; other formats
            shown as "Soon"). Share copies the map's internal (SSO-gated) URL —
            per the decision log, sharing = copy internal link, no tokens. */}
        <button className="btn btn-outline btn-sm" onClick={() => setShowExport(true)}>
          <Download size={13} /> Export
        </button>
        <button className="btn btn-primary btn-sm" onClick={handleShare}>
          <Share2 size={13} /> Share
        </button>
      </div>

      <div className="editor-body">
        {/* Groups panel */}
        <aside className="groups-panel">
          <div className="panel-heading">
            Groups
            <div className="panel-heading-actions">
              <DatasetPicker onSelect={addDatasetToMap} />
              <button className="btn btn-sm btn-outline" onClick={addGroup}>
                <Plus size={12} /> Add
              </button>
            </div>
          </div>

          {groups.length === 0 && (
            <div className="groups-empty">
              No groups yet. Add a group, then type ZIPs, cities, counties, or a
              radius to build it.
            </div>
          )}

          {groups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              mapId={doc.id}
              metrics={shapeMetrics[group.id]}
              expanded={expandedGroupId === group.id}
              selected={selectedArea?.groupId === group.id}
              onToggleExpand={toggleExpand}
              onUpdateGroup={updateGroup}
              onAddAreas={addAreas}
              onRemoveArea={removeArea}
              onDeleteGroup={deleteGroup}
            />
          ))}

          {/* Datasets on this map (mockups/03-editor.html §208). Read-only
              summary from GET /api/maps/:id (doc.datasets — getLinkedDatasets):
              datasets linked to this map via the join table or a group's
              source_dataset_id. Each row opens the Datasets library. Hidden when
              the map uses none. */}
          {doc.datasets && doc.datasets.length > 0 && (
            <>
              <div className="panel-heading" style={{ marginTop: 'var(--space-2)' }}>
                Datasets on this map
              </div>
              {doc.datasets.map((ds) => (
                <button
                  key={ds.id}
                  className="dataset-link"
                  onClick={() => navigate('/datasets')}
                  title={`Open “${ds.name}” in the Datasets library`}
                >
                  <Database size={14} />
                  <span className="dataset-link-body">
                    <span className="dataset-link-name">{ds.name}</span>
                    <span className="badge badge-info dataset-link-count">
                      {formatNumber(ds.row_count)} {ds.row_count === 1 ? 'row' : 'rows'}
                    </span>
                  </span>
                </button>
              ))}
            </>
          )}
        </aside>

        {/* Map canvas */}
        <div className="map-canvas">
          <MapCanvas
            ref={mapCanvasRef}
            mapDoc={doc}
            shapeMetrics={shapeMetrics}
            onShapeDrawn={handleShapeDrawn}
            selectedArea={selectedArea}
            onSelectArea={setSelectedArea}
            onDeleteArea={removeArea}
            onEditRadius={editAreaRadius}
            onReshapeCommit={commitAreaReshape}
          />

          {/* Draw toolbar — each button starts a Geoman draw mode on the canvas.
              The finished shape becomes a new auto-named group (handleShapeDrawn).
              Icons kept per the mock-up: PencilLine (freehand polygon), Square
              (rectangle), CircleDot (radius circle). */}
          <div className="map-float-tl">
            <button
              className="btn btn-outline btn-sm"
              title="Draw a freeform area — click to place points, click the first point to finish"
              onClick={startDrawPolygon}
            >
              <PencilLine size={13} /> Draw area
            </button>
            <button
              className="btn btn-outline btn-sm"
              title="Draw a rectangle — click and drag"
              onClick={startDrawRectangle}
            >
              <Square size={13} /> Rectangle
            </button>
            <button
              className="btn btn-outline btn-sm"
              title="Radius — click the map to place a center point, then type the exact radius"
              onClick={startDrawCircle}
            >
              <CircleDot size={13} /> Radius
            </button>
          </div>

          {/* Legend — driven by the live groups. */}
          {groups.some((g) => g.is_visible !== false) && (
            <div className="map-legend">
              {groups
                .filter((g) => g.is_visible !== false)
                .map((g) => (
                  <div key={g.id}>
                    <span style={{ background: g.color || '#2563EB' }} />
                    {g.name || 'Group'}
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Porter assistant — query/lookup chat. "Add to map" builds a group and
            appends it via addGroupWithAreas (autosave persists it). Passes
            porterAreaContext's geometry (selected shape, or the last drawn
            one) so get_drawn_area / use_drawn_area has a shape to read, and
            its label so PorterPanel can show the user which area that is. */}
        <PorterPanel
          onAddGroup={addGroupWithAreas}
          mapClientId={doc.client_id || null}
          nextGroupIndex={groups.length}
          drawnArea={porterAreaContext?.geometry || null}
          areaLabel={porterAreaContext?.label || null}
        />
      </div>

      {showExport && <ExportModal mapId={doc.id} onClose={() => setShowExport(false)} />}
    </div>
  );
}

// Saved indicator matching the mock-up's .saved-note. States: saving, dirty,
// error, and the default "Saved just now".
function SavedNote({ status }) {
  if (status === 'saving') {
    return (
      <span className="saved-note">
        <Loader2 size={12} className="spin" /> Saving…
      </span>
    );
  }
  if (status === 'dirty') {
    return <span className="saved-note saved-note-muted">Unsaved changes</span>;
  }
  if (status === 'error') {
    return <span className="saved-note saved-note-error">Save failed</span>;
  }
  return (
    <span className="saved-note">
      <Check size={12} /> Saved just now
    </span>
  );
}

// Client-side id for a new group (server assigns final ids on save).
function mintId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

// saveMap is a document-style replace: it deletes every group/area and
// re-inserts them, so EVERY autosave mints brand-new UUIDs for the whole
// document. An id captured before a save (a selection, an expanded group) is
// silently orphaned the instant that save completes — nothing re-points it,
// so it just stops matching anything. Position is what's actually stable:
// saveMap preserves array order via sort_order (falling back to index), and
// getMap reads groups/areas back ordered the same way — so the Nth group
// before a save is still the Nth group after it, just under a new id. These
// re-map a captured reference across that boundary; called from persist()
// right before adopting the server's response.
function remapGroupId(prevDoc, newDoc, groupId) {
  if (!groupId) return groupId;
  const idx = (prevDoc?.groups || []).findIndex((g) => g.id === groupId);
  if (idx === -1) return groupId; // not something we tracked pre-save — leave it alone
  return newDoc?.groups?.[idx]?.id ?? null;
}

function remapAreaSelection(prevDoc, newDoc, selected) {
  if (!selected) return selected;
  const groupIdx = (prevDoc?.groups || []).findIndex((g) => g.id === selected.groupId);
  if (groupIdx === -1) return selected;
  const prevAreas = prevDoc.groups[groupIdx].areas || [];
  const areaIdx = prevAreas.findIndex((a) => a.id === selected.areaId);
  if (areaIdx === -1) return selected;
  const newGroup = newDoc?.groups?.[groupIdx];
  const newArea = newGroup?.areas?.[areaIdx];
  if (!newGroup || !newArea) return null; // the group/area didn't come back — clear rather than dangle
  return { groupId: newGroup.id, areaId: newArea.id };
}

// ─── Client picker (topbar) ───────────────────────────────────────────────────
// A lightweight popover (the same anchored-dropdown pattern as GroupCard's kebab
// menu — no new design-system component) listing Teamwork-synced clients from
// GET /api/clients. Selecting one (or "No client") calls onSelect, which the
// Editor threads into the map document + autosave. Clients are fetched lazily on
// first open so a map that's never assigned costs no request.
function ClientPicker({ clientId, clientName, onSelect }) {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  // Load clients the first time the popover opens.
  useEffect(() => {
    if (!open || clients !== null || loading) return;
    let cancelled = false;
    setLoading(true);
    api
      .get('/clients')
      .then((data) => {
        if (cancelled) return;
        setClients(Array.isArray(data?.clients) ? data.clients : []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Could not load clients.');
        setClients([]); // avoid a retry loop; the error message is shown instead
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, clients, loading]);

  // Close on outside click or Escape while open.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = (clients || []).filter((c) =>
    (c.name || '').toLowerCase().includes(query.trim().toLowerCase())
  );

  const choose = (client) => {
    onSelect(client);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="client-picker" ref={wrapRef}>
      <button
        className="btn btn-outline btn-sm"
        title="Assign this map to a client"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Briefcase size={12} /> {clientName || 'No client'}
        <ChevronDown size={12} />
      </button>

      {open && (
        <div className="client-menu" role="listbox" aria-label="Assign client">
          <div className="client-menu-search">
            <Search size={13} />
            <input
              className="input"
              type="text"
              placeholder="Search clients…"
              value={query}
              autoFocus
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {/* Clear assignment — always offered; disabled state reads via aria. */}
          <button
            className={`client-item${!clientId ? ' is-selected' : ''}`}
            role="option"
            aria-selected={!clientId}
            onClick={() => choose(null)}
          >
            <X size={13} /> No client
          </button>

          <div className="client-menu-list">
            {loading && (
              <div className="client-menu-note">
                <Loader2 size={13} className="spin" /> Loading clients…
              </div>
            )}
            {!loading && error && <div className="client-menu-note">{error}</div>}
            {!loading && !error && filtered.length === 0 && (
              <div className="client-menu-note">
                {query ? 'No matching clients.' : 'No clients synced yet.'}
              </div>
            )}
            {!loading &&
              !error &&
              filtered.map((c) => (
                <button
                  key={c.id}
                  className={`client-item${c.id === clientId ? ' is-selected' : ''}`}
                  role="option"
                  aria-selected={c.id === clientId}
                  onClick={() => choose(c)}
                >
                  <Briefcase size={13} /> {c.name}
                  {c.id === clientId && <Check size={13} className="client-item-check" />}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * DatasetPicker — "+ From dataset" trigger in the groups panel header, for
 * adding an already-saved dataset to this map (the retroactive counterpart to
 * Porter's immediate "Add to map" — see Editor's addDatasetToMap). Same
 * popover mechanics as ClientPicker (search, outside-click/Escape to close),
 * duplicated under its own class names rather than sharing ClientPicker's:
 * the two pick unrelated things and happen to look alike, not a case for
 * forcing one generic component.
 *
 * Tiered datasets are filtered out client-side — they have no single area
 * type (see mapsService.addDatasetToMap), so the server would reject them;
 * simplest to not offer them at all rather than let the user hit that error.
 *
 * @param {object} props
 * @param {(datasetId: string) => Promise<void>} props.onSelect
 */
function DatasetPicker({ onSelect }) {
  const [open, setOpen] = useState(false);
  const [datasets, setDatasets] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  // The specific dataset id being added (not just a boolean) so its row can
  // show a spinner — a large dataset's POST /api/maps/:id/datasets can take
  // a while, and the row previously just went disabled with no other signal.
  const [addingId, setAddingId] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open || datasets !== null || loading) return;
    let cancelled = false;
    setLoading(true);
    api
      .get('/datasets?limit=100')
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data?.datasets) ? data.datasets : [];
        // Tiered datasets have no single area_type — see the JSDoc above.
        setDatasets(rows.filter((d) => d.dataset_type !== 'tiered'));
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Could not load datasets.');
        setDatasets([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, datasets, loading]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = (datasets || []).filter((d) =>
    (d.name || '').toLowerCase().includes(query.trim().toLowerCase())
  );

  const choose = async (dataset) => {
    if (addingId) return;
    setAddingId(dataset.id);
    try {
      await onSelect(dataset.id);
      setOpen(false);
      setQuery('');
    } catch {
      // onSelect (Editor's addDatasetToMap) already toasts the error; leave
      // the popover open so the user can pick a different dataset or retry.
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div className="dataset-picker" ref={wrapRef}>
      <button
        className="btn btn-sm btn-outline"
        title="Add an existing dataset to this map"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Database size={12} /> From dataset
      </button>

      {open && (
        <div className="dataset-picker-menu" role="listbox" aria-label="Add a dataset to this map">
          <div className="dataset-picker-search">
            <Search size={13} />
            <input
              className="input"
              type="text"
              placeholder="Search datasets…"
              value={query}
              autoFocus
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="dataset-picker-list">
            {loading && (
              <div className="dataset-picker-note">
                <Loader2 size={13} className="spin" /> Loading datasets…
              </div>
            )}
            {!loading && error && <div className="dataset-picker-note">{error}</div>}
            {!loading && !error && filtered.length === 0 && (
              <div className="dataset-picker-note">
                {query ? 'No matching datasets.' : 'No datasets saved yet.'}
              </div>
            )}
            {!loading &&
              !error &&
              filtered.map((d) => (
                <button
                  key={d.id}
                  className="dataset-picker-item"
                  role="option"
                  aria-selected={false}
                  disabled={Boolean(addingId)}
                  onClick={() => choose(d)}
                >
                  {addingId === d.id ? (
                    <Loader2 size={13} className="spin" />
                  ) : (
                    <Database size={13} />
                  )}
                  <span className="dataset-picker-item-name">{d.name}</span>
                  <span className="badge badge-info">{formatNumber(d.row_count)} rows</span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
